import React from 'react';
import { useBeats } from '../../hooks/useBeats';
import { trackingClip } from '../../data/tracking';
const TEETH = [{
  x: 8,
  y: 30
}, {
  x: 30,
  y: 23
}, {
  x: 52,
  y: 20
}, {
  x: 74,
  y: 23
}, {
  x: 96,
  y: 30
}];

/** The tooth that ends up off-plan. */
const OFF_PLAN_INDEX = 3;
interface ArchProps {
  /** When true the aligner stays on plan while one tooth sits short of it. */
  deviated: boolean;
}
function Arch({
  deviated
}: ArchProps) {
  return <svg viewBox="0 0 118 62" className="h-[62px] w-full" aria-hidden="true" focusable="false" data-id="element-127" data-mp="2t5mgydetq04u">
      {/* Aligner shell — always in the planned position */}
      <path d="M4 34 C4 20 16 12 30 10 C44 8 74 8 88 10 C102 12 114 20 114 34" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2.5" strokeLinecap="round" data-id="element-128" data-mp="323olv41ohcxe" />
      {TEETH.map((tooth, index) => {
      const off = deviated && index === OFF_PLAN_INDEX;
      return <rect key={index} x={tooth.x} y={tooth.y} width="14" height="22" rx="4" fill={off ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.82)'} style={{
        transform: off ? 'translateY(6px) rotate(5deg)' : 'none',
        transformOrigin: `${tooth.x + 7}px ${tooth.y + 18}px`,
        transition: 'transform 260ms cubic-bezier(0.23, 1, 0.32, 1)'
      }} data-id="element-129" data-mp="3t8hrof4wsltw" data-mpi={index} />;
    })}
      {deviated && <circle cx={TEETH[OFF_PLAN_INDEX].x + 7} cy={TEETH[OFF_PLAN_INDEX].y + 4} r="9" fill="none" stroke="#05dd4d" strokeWidth="1.5" className="animate-fade-in" data-id="element-130" data-mp="krwm78bhkavj" />}
    </svg>;
}
const BEATS = [500, 1100];

/**
 * Page 2 visual: the same case, as planned and as delivered, with one small
 * visible discrepancy — the aligner on plan, the tooth short of it.
 */
export function PlannedActual() {
  const beat = useBeats(BEATS);
  return <figure className="w-full max-w-[380px]" data-id="element-131" data-mp="2oi1c844h62r5">
      <div className="flex items-stretch gap-2" data-id="element-132" data-mp="2kg7eo4pktev7">
        {(['Planned', 'Actual'] as const).map((label, index) => {
        const isActual = index === 1;
        return <React.Fragment key={label} data-id="element-133" data-mp="2ydchzwxwnzmr" data-mpi={index}>
              {isActual && <div className="flex flex-shrink-0 items-center" data-id="element-134" data-mp="2tf19pqn9dx6n">
                  <span className={`font-body text-sm text-white/30 transition-opacity duration-200 ease-out ${beat >= 1 ? 'opacity-100' : 'opacity-0'}`} data-id="element-135" data-mp="37flsimt6bt05">
                    →
                  </span>
                </div>}
              <div className={`flex-1 rounded-xl border px-3 pb-3 pt-2.5 transition-[opacity,border-color,transform] duration-200 ease-out ${isActual ? beat >= 1 ? 'translate-y-0 border-solventum-primary/40 opacity-100' : 'translate-y-1 border-white/10 opacity-0' : 'border-white/15 opacity-100'}`} data-id="element-136" data-mp="3c8eu51tcil80">
                <p className={`font-body text-[10px] uppercase tracking-[0.18em] ${isActual ? 'text-solventum-primary' : 'text-white/40'}`} data-id="element-137" data-mp="3vsvtqkyebqto">
                  {label}
                </p>
                <div className="mt-2" data-id="element-138" data-mp="3m1svrs0rjrym">
                  <Arch deviated={isActual && beat >= 2} data-id="element-139" data-mp="3qtkddd1xyncm" />
                </div>
              </div>
            </React.Fragment>;
      })}
      </div>

      <img src={trackingClip.image} alt={trackingClip.alt} className={`mt-3 h-24 w-full rounded-xl object-cover transition-opacity duration-300 ease-out md:h-32 ${beat >= 1 ? 'opacity-100' : 'opacity-0'}`} data-id="element-140" data-mp="3d48u4hyzntnh" />
      <figcaption className="mt-2 font-body text-[11px] text-white/35" data-id="element-141" data-mp="383ks71981seh">
        A small discrepancy between the plan and the movement delivered.
      </figcaption>
    </figure>;
}